import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ClaimSubject } from '@/versions/v1/schema/claim';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import {
  acceptVersion,
  confirmAllTags,
  confirmTag,
  INITIAL_CLAIMS_STATE,
  rule,
  setTagConfirmed,
  type ClaimsSessionState,
} from './ClaimsEngine';

type ClaimsContextValue = ClaimsSessionState & {
  rule: (
    subject: ClaimSubject,
    acceptsClaimId: string,
    reason: string,
    author: { role: PlantRoleId; name?: string },
    at?: string,
  ) => void;
  acceptVersion: (
    at: string,
    by: { role: PlantRoleId; name?: string },
    revisedBalance?: boolean,
  ) => void;
  confirmTag: (tagId: string) => void;
  setTagConfirmed: (tagId: string, confirmed: boolean) => void;
  confirmAllTags: (tagIds: readonly string[]) => void;
};

const ClaimsContext = createContext<ClaimsContextValue | null>(null);

export function ClaimsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ClaimsSessionState>(INITIAL_CLAIMS_STATE);

  const onRule = useCallback(
    (
      subject: ClaimSubject,
      acceptsClaimId: string,
      reason: string,
      author: { role: PlantRoleId; name?: string },
      at = '2026-09-03',
    ) => {
      setState((current) => rule(current, subject, acceptsClaimId, reason, author, at));
    },
    [],
  );

  const onAcceptVersion = useCallback(
    (
      at: string,
      by: { role: PlantRoleId; name?: string },
      revisedBalance = false,
    ) => {
      setState((current) => acceptVersion(current, at, by, revisedBalance));
    },
    [],
  );

  const onConfirmTag = useCallback((tagId: string) => {
    setState((current) => confirmTag(current, tagId));
  }, []);

  const onSetTagConfirmed = useCallback((tagId: string, confirmed: boolean) => {
    setState((current) => setTagConfirmed(current, tagId, confirmed));
  }, []);

  const onConfirmAllTags = useCallback((tagIds: readonly string[]) => {
    setState((current) => confirmAllTags(current, tagIds));
  }, []);

  const value = useMemo<ClaimsContextValue>(
    () => ({
      ...state,
      rule: onRule,
      acceptVersion: onAcceptVersion,
      confirmTag: onConfirmTag,
      setTagConfirmed: onSetTagConfirmed,
      confirmAllTags: onConfirmAllTags,
    }),
    [state, onRule, onAcceptVersion, onConfirmTag, onSetTagConfirmed, onConfirmAllTags],
  );

  return <ClaimsContext.Provider value={value}>{children}</ClaimsContext.Provider>;
}

export function useClaims() {
  const ctx = useContext(ClaimsContext);
  if (!ctx) throw new Error('useClaims must be used inside <ClaimsProvider>');
  return ctx;
}
